<template>
  <div :class="columnClassName">
    <column-header v-if="showColumnHeader" :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
				<div class="col-md-12">
          <h3>{{ widget.name }}</h3>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 leftcol">
          <div class="form-group">
            <label for="name">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="widgetName" v-model="widget.name" :placeholder="_('Enter widget name')">
          </div>
        </div>
        <div class="col-sm-6">
          <div class="form-group">
            <label for="name">{{ _('Type') }}</label>
            <select class="form-control" id="widgetType" v-model="widgetType">
              <option value="Basket">{{ _('Basket') }}</option>
              <option value="Category">{{ _('Category') }}</option>
              <option value="ProductCard">{{ _('Product card') }}</option>
              <option value="Product">{{ _('Product') }}</option>
              <option value="Search">{{ _('Search') }}</option>
              <option value="Login">{{ _('Login') }}</option>
              <option value="Account">{{ _('Account') }}</option>
              <option value="JoinChannel">{{ _('Join sales channel') }}</option>
            </select>
          </div>
        </div>
      </div>
      <component :is="widgetEditorType" :baseDomain="baseDomain" :shopId="shopId" :apiKey="apiKey" :version="version" v-on:editorInput="setWidgetData" :value="widget.data" :widget="widget" :type="widgetType"></component>
      <div class="row">
        <div class="col-sm-12">
          <div class="form-group">
            <label>{{ _('Embed script') }}</label>
            <textarea rows="1" style="font-family: monospace" class="form-control" readonly v-model="embedScript"></textarea>
            <button class="btn btn-success" type="button" v-on:click="copyToClipboard(embedScript)"><i class="fa fa-fw fa-clipboard"></i> {{ _('Copy to clipboard') }}</button>
          </div>
          <div class="form-group">
            <label>{{ _('Embed code') }}</label>
            <textarea rows="1" style="font-family: monospace" class="form-control" readonly v-model="embedCode"></textarea>
            <button class="btn btn-success" type="button" v-on:click="copyToClipboard(embedCode)"><i class="fa fa-fw fa-clipboard"></i> {{ _('Copy to clipboard') }}</button>
          </div>
          <div class="well">
            {{ _('Place the embed script in the HEAD section of each page you intend to use Johku Widgets on.') }}
            {{ _('Then place the embed code in the BODY of the page where you want the widget to appear.') }}
            {{ _('You can have multiple embed codes on one page, but only one embed script.') }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12">
          <div class="form-group">
            <label>{{ _('CSS') }}</label>
            <textarea rows="10" style="font-family: monospace" class="form-control" autocorrect="off" spellcheck="false" v-model="widget.css"></textarea>
          </div>
          <div class="well">
            {{ _('You can apply custom CSS styles to the widget by pasting the styles into this field.') }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12">
          <div class="form-group">
            <label>{{ _('Restrict embedding to specific URLs') }}</label>
            <textarea rows="5" style="font-family: monospace" class="form-control" autocorrect="off" spellcheck="false" v-model="widget.restrict"></textarea>
          </div>
          <div class="well">
            {{ _('You can restrict the embedding of the widget to specific URLs by entering them into this field. Each URL must be on a new line and may contain wildcards.') }}
          </div>
        </div>
      </div>
      <div class="row" style="margin-bottom: 10px">
        <div class="col-sm-12">
          <label>{{ _('Embeddable widget preview') }}</label>
          <p>{{ _('The preview will update after saving changes.') }}</p>
        </div>
        <div class="col-sm-12" v-if="isPreviewVisible">
          <johku-widget :id="shopId + '/' + widget.identifier" :locale="version" :data="widgetData"></johku-widget>
        </div>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" type="button" v-on:click="remove" v-if="widget.id"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="flex-row">
          <button class="btn btn-default" type="button" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import { mapGetters } from 'vuex'
import components from '~/lib/components'
import notifications from '~/lib/notifications'
import widgets from '~/lib/widgets'
const CategoryEditor = defineAsyncComponent({ loader: () => import('~/components/widgeteditors/CategoryEditor'), suspensible: false })
const JoinChannelEditor = defineAsyncComponent({ loader: () => import('~/components/widgeteditors/JoinChannelEditor'), suspensible: false })
const ProductEditor = defineAsyncComponent({ loader: () => import('~/components/widgeteditors/ProductEditor'), suspensible: false })
const SearchEditor = defineAsyncComponent({ loader: () => import('~/components/widgeteditors/SearchEditor'), suspensible: false })
const NoEditor = defineAsyncComponent({ loader: () => import('~/components/componenteditors/NoEditor'), suspensible: false })

export default {
  data () {
    return {
      widget: {},
      data: {}
    }
  },
  mixins: [components, notifications],
  computed: {
    ...mapGetters(['activeBasket', 'activeOrder', 'clientid', 'apiKey', 'pos', 'posDeliveryActive', 'locale', 'baseDomain', 'version', 'shopId']),
    embedScript () {
      return '<script src="https://johku.com/widget.js"><' + '/script>'
    },
    embedCode () {
      return '<johku-widget id="' + this.shopId + '/' + this.widget.identifier + '" locale="' + this.version + '"></johku-widget>'
    },
    basketId () {
      return this.$store.state.basketIds[this.shopId + '-' + this.$store.state.activeBasket]
    },
    widgetData () {
      if (this.widgetType === 'Login' || this.widgetType === 'Account') {
        return '{}'
      }
      return JSON.stringify({
        basketId: this.basketId,
        orderIdentifier: this.activeOrder ? this.activeOrder.identifier : null,
        clientId: this.clientid,
        masterShopId: this.shopId,
        index: this.activeBasket,
        pos: this.pos,
        posDeliveryActive: this.posDeliveryActive,
        apiKey: this.apiKey
      })
    },    
    widgetType: {
      get () {
        if (this.widget && this.widget.type) {
          return this.widget.type
        }
        return 'Category'
      },
      set (newValue) {
        this.widget.type = newValue
      }
    },
    widgetEditorType () {
      if(this.loading) {
        return NoEditor
      }
      switch(this.widget.type) {
        case 'Category':
          return CategoryEditor
        case 'Product':
        case 'ProductCard':
          return ProductEditor
        case 'JoinChannel':
          return JoinChannelEditor
        case 'Search':
          return SearchEditor
        default:
          return NoEditor
      }
    },
    isPreviewVisible () {
      return this.widget && this.widget.id && !this.saving
    }
  },
  methods: {
    setWidgetData (data) {
      this.widget.data = data
    },
    async copyToClipboard(data) {
      try {
        let status = await this.$copyText(data)
        this.showNotification(this._('Code copied to clipboard!'))
      } catch(e) {
        this.showError(e)
      }
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'identifier',
        'data',
        'css',
        'restrict'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.widget[e], a), {})
      data.type = this.widgetType
      return data
    },
    async doRemove () {
      let response
      try {
        response = await axios.delete(this.baseDomain + this.shopId + '/widgets/' + this.widget.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        return this.showError(e)
      }
      this.close()
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/widgets/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/widgets.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadWidget(response.data.id)
      }
      this.saving = false
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.layout)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    loadWidget (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/widgets/' + id + '.json?apikey=' + this.apiKey)
        } catch (e) {
          this.showError(e)
          reject()
          this.loading = false
          return
        }
        this.widget = response.data
        if (this.widget.data) {
          this.data = JSON.parse(this.widget.data)
        }
        this.storeState()
        this.loading = false
        resolve(this.widget)
      })
    }
  },
  components: {
    NoEditor: () => import('~/components/componenteditors/NoEditor'),
    CategoryEditor: () => import('~/components/widgeteditors/CategoryEditor'),
    JoinChannelEditor: () => import('~/components/widgeteditors/JoinChannelEditor'),
    ProductEditor: () => import('~/components/widgeteditors/ProductEditor'),
    SearchEditor: () => import('~/components/widgeteditors/SearchEditor')
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadWidget(this.component.initData.widgetId)
    }
  },
  mounted () {
    if(!this.events) {
      let events = {}
      events['widgetUpdate'] = async (message) => {
        if (message && message.data && this.widget && parseInt(message.data.id, 10) === parseInt(this.widget.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadWidget(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.widget && this.widget.id && parseInt(this.widget.id, 10) !== parseInt(this.component.initData.widgetId, 10)) {
        try {
          await this.loadWidget(this.component.initData.widgetId)
        } catch(e) {}
      }
    }
  }
}
</script>